export interface SessionStats {
    startTime: number;
    endTime?: number;
    activeTimePerMod: Record<string, number>;
    memorySnapshots: { time: number; usedJSHeapSize: number; totalJSHeapSize: number }[];
}

class StatsManagerClass {
    private currentSession: SessionStats;
    private currentMod: string = 'home';
    private modEnterTime: number = Date.now();
    private memoryInterval: any;

    constructor() {
        this.currentSession = {
            startTime: Date.now(),
            activeTimePerMod: {},
            memorySnapshots: []
        };
    }

    public init() {
        if (typeof window === 'undefined') return;
        
        // Load previous session if any
        const prev = localStorage.getItem('goodlyfe_session_stats');
        if (!prev) {
            // Seed a dummy previous session if it's the very first time
            localStorage.setItem('goodlyfe_previous_session', JSON.stringify({
                startTime: Date.now() - 1000 * 60 * 60, // 1 hr ago
                endTime: Date.now() - 1000 * 60 * 10,
                activeTimePerMod: { home: 120000, profile: 45000, search: 12000 },
                memorySnapshots: []
            }));
        }

        // Start memory polling
        this.memoryInterval = setInterval(() => {
            this.snapshotMemory();
        }, 5000);

        // Listen for visibility changes to save session
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden') {
                this.saveSession();
            } else {
                // We came back, update start time so we don't count background time? 
                // For simplicity, we just resume counting.
                this.modEnterTime = Date.now();
            }
        });
        
        window.addEventListener('beforeunload', () => {
            this.saveSession();
        });
    }

    public setActiveMod(modName: string) {
        const now = Date.now();
        const spent = now - this.modEnterTime;
        
        if (this.currentSession.activeTimePerMod[this.currentMod]) {
            this.currentSession.activeTimePerMod[this.currentMod] += spent;
        } else {
            this.currentSession.activeTimePerMod[this.currentMod] = spent;
        }

        this.currentMod = modName;
        this.modEnterTime = now;
        this.saveCurrentSessionTemp(); // backup on switch
    }

    private snapshotMemory() {
        const perf = (window.performance as any).memory;
        if (perf) {
            this.currentSession.memorySnapshots.push({
                time: Date.now(),
                usedJSHeapSize: perf.usedJSHeapSize,
                totalJSHeapSize: perf.totalJSHeapSize
            });
            // Keep only last 100 snapshots
            if (this.currentSession.memorySnapshots.length > 100) {
                this.currentSession.memorySnapshots.shift();
            }
        }
    }
    
    // Periodically update the "current" mod's time to keep stats accurate without switching
    public flushCurrentModTime() {
        const now = Date.now();
        const spent = now - this.modEnterTime;
        if (this.currentSession.activeTimePerMod[this.currentMod]) {
            this.currentSession.activeTimePerMod[this.currentMod] += spent;
        } else {
            this.currentSession.activeTimePerMod[this.currentMod] = spent;
        }
        this.modEnterTime = now;
    }

    private saveCurrentSessionTemp() {
        this.flushCurrentModTime();
        localStorage.setItem('goodlyfe_current_session', JSON.stringify(this.currentSession));
    }

    private saveSession() {
        this.flushCurrentModTime();
        this.currentSession.endTime = Date.now();
        localStorage.setItem('goodlyfe_previous_session', JSON.stringify(this.currentSession));
    }

    public getStats() {
        this.flushCurrentModTime();
        const prevStr = localStorage.getItem('goodlyfe_previous_session');
        const curr = this.currentSession;
        return {
            current: curr,
            previous: prevStr ? JSON.parse(prevStr) : null
        };
    }
}

export const StatsManager = new StatsManagerClass();
